import { useState, useEffect, useCallback, useRef } from 'react';
import { cartPrice } from '../utils/StringUtils';

import { updateUserWithFidelityCard } from '../actions/userActions';
import { changeJackpotAmountMatch } from '../actions/cartActions';

import { useDispatch } from 'react-redux';
import { useLocalStorage } from './useLocalStorage';
import { calculateTotalAddedToJackpot } from '../utils/order/orderUtils';

export const useJackpotMatch = ({
  axiosInstance,
  user,
  cartItems,
  store,
  shouldFetch = false
}) => {

  let initialMontantSolde = null;
  if (user?.fidCard?.[0]?.fidelityInfo?.ficheClient) {
    initialMontantSolde = user?.fidCard?.[0]?.fidelityInfo?.ficheClient?.soldeCagnotte?.montantSolde;
  }

  const dispatch = useDispatch();
  const isUpdatingJackpotRef = useRef(false);
  const userRef = useRef(user);
  const axiosInstanceRef = useRef(axiosInstance);
  const [hasManuallySetAmount, setHasManuallySetAmount] = useLocalStorage('hasManuallySetJackpotAmount', false);
  const [manualJackpotAmount, setManualJackpotAmount] = useLocalStorage('manualJackpotAmount', null);

  useEffect(() => {
    userRef.current = user;
    axiosInstanceRef.current = axiosInstance;
  }, [user, axiosInstance]);

  const [isJackpotMatchActive, setIsJackpotMatchActive] = useState(
    Boolean(cartItems?.jackpot?.isActive)
  );
  const [isLoadingFidCards, setIsLoadingFidCards] = useState(false);
  const [errorMessageFidCards, setErrorMessageFidCards] = useState(null);
  const [jackpotMatchSolde, setJackpotMatchSolde] = useState(initialMontantSolde);
  const [jackpotMatchAmount, setJackpotMatchAmount] = useState(() => {
    if (hasManuallySetAmount && manualJackpotAmount !== null) {
      return manualJackpotAmount;
    }
    return cartItems?.jackpot?.amount || null;
  });
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [isFirstVisitLoading, setIsFirstVisitLoading] = useState(false);
  const isFirstVisitCheckedRef = useRef(false);

  const getInformationFidCards = useCallback(async () => {
    const currentUser = userRef.current;
    const currentAxiosInstance = axiosInstanceRef.current;

    setIsLoadingFidCards(true);
    let montantSolde = null;

    if (!currentUser?.fidCard?.[0]?.fidelityInfo?.ficheClient) {
      console.error('updateOrderJackpotMatch: Fidélité card not found');
      return null;
    }

    try {
      const numeroCarte = currentUser?.fidCard?.[0]?.fidelityInfo?.ficheClient?.numeroCarte;
      const response = await currentAxiosInstance.get(`/fidelity-card/getFidelityCardInfo/${numeroCarte}`);

      if (response.data?.data?.content?.statutIdentification === 'OK') {
        const fidelityCardData = { ...currentUser?.fidCard?.[0] }
        fidelityCardData.fidelityInfo.ficheClient = response.data?.data?.content?.ficheClient

        if (response.data?.data?.content?.ficheClient?.soldeCagnotte?.montantSolde > 0) {
          montantSolde = response.data?.data?.content?.ficheClient?.soldeCagnotte?.montantSolde;

          setJackpotMatchSolde(montantSolde);
          setErrorMessageFidCards(null);
        }
        else {
          setJackpotMatchSolde(0);
          setErrorMessageFidCards(null);
        }

        dispatch(updateUserWithFidelityCard(fidelityCardData));
      }
      else {
        setErrorMessageFidCards("Cagnotte indisponible");
        setJackpotMatchSolde(null);
      }
    } catch (error) {
      console.error("getInformationFidCards", error);
      setErrorMessageFidCards("Erreur lors de la récupération de la cagnotte");
    } finally {
      setIsLoadingFidCards(false);
    }

    return montantSolde;
  }, [dispatch]);

  const updateOrderJackpotMatch = useCallback(async ({ orderId, amount, isActive, isAvailable }) => {
    const currentUser = userRef.current;
    const currentAxiosInstance = axiosInstanceRef.current;

    if (!currentUser?.fidCard?.[0]?.fidelityInfo?.ficheClient) {
      console.error('updateOrderJackpotMatch: Fidélité card not found');
      return null;
    }

    if (!orderId) {
      console.error('updateOrderJackpotMatch: Order ID not found');
      return null;
    }

    setIsLoadingFidCards(true);

    try {
      const { nom, prenom } = currentUser.fidCard[0].fidelityInfo.ficheClient;

      const response = await currentAxiosInstance.put(`/orders/${orderId}/jackpot`, {
        amount,
        nom,
        prenom,
        isActive,
        isAvailable,
      });

      if (response.data.success && response.data.order) {
        dispatch(changeJackpotAmountMatch({ order: response.data.order }));
      }

      return response.data;
    } catch (error) {
      console.error("updateOrderJackpotMatch", error);
      setErrorMessageFidCards("Erreur lors de la mise à jour de la commande");
      return null;
    } finally {
      setIsLoadingFidCards(false);
    }
  }, []);

  const get10PercentOfCartPrice = useCallback(() => {
    if (!Number.isFinite(cartPrice(cartItems))) {
      throw new Error("Invalid arguments for get10PercentOfCartPrice");
    }

    const cartPriceItems = cartPrice(cartItems);

    const cashback = cartPriceItems * 10 / 100;

    return cashback.toFixed(2);

  }, [cartItems]);


  const hasProductsWithCashback = useCallback(() => {
    if (!cartItems?.orderItems) {
      return false;
    }
    return cartItems.orderItems.some(item => item.cashbackAmount > 0);
  }, [cartItems]);


  const totalAddedToJackpot = useCallback(() => {
    if (!cartItems?.orderItems) {
      return 0;
    }

    const hasProductsWithCashback = cartItems.orderItems.some(item => item.cashbackAmount > 0);

    if (!hasProductsWithCashback) {
      return get10PercentOfCartPrice();
    }

    return calculateTotalAddedToJackpot(cartItems.orderItems);
  }, [cartItems]);


  const get100PercentOfCartPrice = useCallback((montantSolde) => {
    if (!Number.isFinite(montantSolde)) {
      throw new Error("Invalid arguments for get100PercentOfCartPrice");
    }
    if (!Number.isFinite(cartPrice(cartItems))) {
      throw new Error("Invalid arguments for get100PercentOfCartPrice");
    }

    const totalCartPrice = cartPrice(cartItems);
    if (montantSolde >= totalCartPrice) {
      return totalCartPrice;
    }
    if (montantSolde > 0) {
      return montantSolde;
    }
    return 0;
  }, [cartItems]);

  const getPercentageOfCartPrice = useCallback((montantSolde, percentage) => {
    if (!Number.isFinite(montantSolde)) {
      throw new Error("Invalid montantSolde for getPercentageOfCartPrice");
    }
    if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
      throw new Error("Invalid percentage for getPercentageOfCartPrice");
    }
    if (!Number.isFinite(cartPrice(cartItems))) {
      throw new Error("Invalid cart price for getPercentageOfCartPrice");
    }

    const totalCartPrice = cartPrice(cartItems);
    const calculatedAmount = (totalCartPrice * percentage) / 100;

    if (calculatedAmount > montantSolde) {
      return montantSolde;
    }
    if (calculatedAmount > 0) {
      return calculatedAmount;
    }
    return 0;
  }, [cartItems]);

  const resetJackpotMatch = useCallback(() => {
    setJackpotMatchAmount(0);
    setIsJackpotMatchActive(false);
    setHasManuallySetAmount(false);
    setManualJackpotAmount(null);
  }, [setHasManuallySetAmount, setManualJackpotAmount]);

  const cartItemsPrice = cartItems?.itemsPrice;
  const cartPromoPrice = cartItems?.promoPrice;

  const updateJackpotInformation = useCallback(async (percentage = 100, isActive = true, isAvailable = true) => {
    if (!cartItems?.itemsPrice || cartItems.itemsPrice === 0) {
      setErrorMessageFidCards("Panier vide");
      resetJackpotMatch();
      return;
    }

    const montantSolde = await getInformationFidCards();
    if (!montantSolde) {
      setErrorMessageFidCards("Cagnotte indisponible");
      resetJackpotMatch();
      return;
    }

    const amount = percentage === 100
      ? get100PercentOfCartPrice(montantSolde)
      : getPercentageOfCartPrice(montantSolde, percentage);

    await updateOrderJackpotMatch({ orderId: cartItems._id, amount, isActive, isAvailable });

    setJackpotMatchAmount(amount);
  }, [cartItems, getInformationFidCards, get100PercentOfCartPrice, getPercentageOfCartPrice, updateOrderJackpotMatch, resetJackpotMatch]);

  const handleJackpotMatch = useCallback(async (event) => {
    if (event) event.stopPropagation();

    if (isJackpotMatchActive) {
      resetJackpotMatch();

      if (cartItems?._id) {
        await updateOrderJackpotMatch({ orderId: cartItems._id, amount: 0, isActive: false, isAvailable: true });
      }

      return;
    }

    setHasManuallySetAmount(false);
    setManualJackpotAmount(null);
    await updateJackpotInformation();

    if (errorMessageFidCards) return

    setIsJackpotMatchActive(true);
  }, [isJackpotMatchActive, cartItems?._id, updateOrderJackpotMatch, updateJackpotInformation, resetJackpotMatch, errorMessageFidCards, setHasManuallySetAmount, setManualJackpotAmount]);

  const updateJackpotAmount = useCallback(async (percentage) => {
    if (!cartItems?._id) {
      console.error('updateJackpotAmount: Order ID not found');
      return;
    }

    await updateJackpotInformation(percentage);
  }, [cartItems?._id, updateJackpotInformation]);

  const checkIsFirstVisit = useCallback(async () => {
    const currentUser = userRef.current;
    const currentAxiosInstance = axiosInstanceRef.current;

    if (!currentUser?._id) {
      setIsFirstVisit(false);
      return false;
    }

    // Éviter les appels multiples
    if (isFirstVisitCheckedRef.current) {
      return isFirstVisit;
    }

    setIsFirstVisitLoading(true);

    try {
      const { data } = await currentAxiosInstance.get(`/user/receipt/${currentUser._id}`);
      const hasZeroVisit = currentUser?.fidCard?.[0]?.fidelityInfo?.ficheClient?.compteurVisite === 0
      if (data.length === 0 && hasZeroVisit) {
        setIsFirstVisit(true);
        isFirstVisitCheckedRef.current = true;
        return true;
      }

      const fidCardNumbers = data
        .map(item => item.fidCardNumber)
        .filter(num => num !== false && num !== null && num !== undefined && num !== '' && typeof num !== 'object');

      const userCardNumber = currentUser?.fidCard?.[0]?.fidelityInfo?.ficheClient?.numeroCarte;
      const isFidCardNumberInReceipt = fidCardNumbers.includes(Number(userCardNumber));
      const result = !isFidCardNumberInReceipt && hasZeroVisit;

      setIsFirstVisit(result);
      isFirstVisitCheckedRef.current = true;
      return result;
    } catch (error) {
      console.error('Error checking first visit:', error);
      setIsFirstVisit(false);
      return false;
    } finally {
      setIsFirstVisitLoading(false);
    }
  }, [isFirstVisit]);


  const updateJackpotAmountByValue = useCallback(async (amountValue) => {
    if (!cartItems?._id) {
      console.error('updateJackpotAmountByValue: Order ID not found');
      return;
    }

    const montantSolde = await getInformationFidCards();
    if (!montantSolde) {
      setErrorMessageFidCards("Cagnotte indisponible");
      return;
    }

    const validAmount = Math.min(Math.max(0, amountValue), montantSolde);
    if (validAmount === 0) {
      setErrorMessageFidCards("Cagnotte insuffisante");
      await updateOrderJackpotMatch({ orderId: cartItems._id, amount: 0, isActive: false, isAvailable: true });
      return;
    }

    await updateOrderJackpotMatch({ orderId: cartItems._id, amount: validAmount, isActive: true, isAvailable: true });

    setHasManuallySetAmount(true);
    setManualJackpotAmount(validAmount);
    setJackpotMatchAmount(validAmount);
  }, [cartItems?._id, getInformationFidCards, updateOrderJackpotMatch, setHasManuallySetAmount, setManualJackpotAmount]);

  useEffect(() => {
    if (user?._id && shouldFetch) {
      getInformationFidCards();
    }
  }, [user?._id, shouldFetch, getInformationFidCards]);

  // Check if it's the user's first visit with this fidelity card
  const userCardNumber = user?.fidCard?.[0]?.fidelityInfo?.ficheClient?.numeroCarte;
  useEffect(() => {
    if (shouldFetch && user?._id && userCardNumber && !isFirstVisitCheckedRef.current) {
      checkIsFirstVisit();
    }
  }, [shouldFetch, user?._id, userCardNumber, checkIsFirstVisit]);

  useEffect(() => {
    if (cartItems?.jackpot?.isActive !== undefined) {
      setIsJackpotMatchActive(cartItems.jackpot.isActive);
    }
    if (cartItems?.jackpot?.amount !== undefined && !hasManuallySetAmount) {
      setJackpotMatchAmount(cartItems.jackpot.amount);
    }
  }, [cartItems?.jackpot?.isActive, cartItems?.jackpot?.amount, hasManuallySetAmount]);

  useEffect(() => {
    if (isJackpotMatchActive && user?._id && jackpotMatchSolde === null && !errorMessageFidCards && !isLoadingFidCards && shouldFetch) {
      getInformationFidCards();
    }
  }, [isJackpotMatchActive, user?._id, jackpotMatchSolde, errorMessageFidCards, isLoadingFidCards, shouldFetch, getInformationFidCards]);

  useEffect(() => {
    if (jackpotMatchSolde !== null && jackpotMatchSolde !== undefined && isJackpotMatchActive) {
      if (jackpotMatchAmount <= jackpotMatchSolde) {
        return;
      }

      let currentJackpotMatchAmount = 0
      if (jackpotMatchAmount > jackpotMatchSolde) currentJackpotMatchAmount = jackpotMatchSolde;

      updateOrderJackpotMatch({ orderId: cartItems._id, amount: currentJackpotMatchAmount, isActive: true, isAvailable: true });
      setJackpotMatchAmount(currentJackpotMatchAmount);
    }
  }, [jackpotMatchSolde, jackpotMatchAmount, isJackpotMatchActive, cartItems, updateOrderJackpotMatch]);

  useEffect(() => {
    const hasRequiredData = isJackpotMatchActive && cartItems?._id && jackpotMatchSolde;
    if (!hasRequiredData || isUpdatingJackpotRef.current) {
      return;
    }

    const hasPromo = cartPromoPrice && cartPromoPrice > 0;
    const currentPrice = hasPromo ? cartPromoPrice : cartItemsPrice;

    if (!Number.isFinite(currentPrice)) {
      return;
    }

    if (currentPrice === 0 || currentPrice < 0) {
      setHasManuallySetAmount(false);
      setManualJackpotAmount(null);
      isUpdatingJackpotRef.current = true;

      updateOrderJackpotMatch({
        orderId: cartItems._id,
        amount: 0,
        isActive: false,
        isAvailable: true
      }).then(() => {
        setJackpotMatchAmount(0);
        setIsJackpotMatchActive(false);
      }).finally(() => {
        isUpdatingJackpotRef.current = false;
      });
      return;
    }

    const maxUsableAmount = Math.min(jackpotMatchSolde, currentPrice);
    const shouldUpdateAmount = jackpotMatchAmount > maxUsableAmount;

    if (shouldUpdateAmount) {
      setHasManuallySetAmount(false);
      setManualJackpotAmount(null);
      isUpdatingJackpotRef.current = true;

      updateOrderJackpotMatch({
        orderId: cartItems._id,
        amount: maxUsableAmount,
        isActive: true,
        isAvailable: true
      }).then(() => {
        setJackpotMatchAmount(maxUsableAmount);
      }).finally(() => {
        isUpdatingJackpotRef.current = false;
      });
    }
  }, [cartItemsPrice, cartPromoPrice, isJackpotMatchActive, cartItems?._id, jackpotMatchSolde, jackpotMatchAmount, updateOrderJackpotMatch, setHasManuallySetAmount, setManualJackpotAmount]);

  const isShouldShowFidMatch = store && store?.api !== 'mongoDb';

  return {
    isJackpotMatchActive,
    isLoadingFidCards,
    errorMessageFidCards,
    jackpotMatchSolde,
    jackpotMatchAmount,
    isShouldShowFidMatch,
    setIsJackpotMatchActive,
    getInformationFidCards,
    updateOrderJackpotMatch,
    get100PercentOfCartPrice,
    getPercentageOfCartPrice,
    resetJackpotMatch,
    updateJackpotInformation,
    handleJackpotMatch,
    updateJackpotAmount,
    updateJackpotAmountByValue,
    totalAddedToJackpot,
    hasProductsWithCashback,
    get10PercentOfCartPrice,
    isFirstVisit,
    isFirstVisitLoading,
    checkIsFirstVisit,
  };
};

